import { Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { ExamplesDataType, ExamplesType, MessageActionStatusType } from 'store/bonoBot/types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { addMessage, bonoBotState } from 'store/bonoBot/bonoBot.slice';
import BotChatIcon from './BotChatIcon';
import Examples from './Examples';

interface IMessageBubbleProps {
   role: 'bot' | 'user';
   text: string;
   examplesType?: ExamplesType;
   examples?: ExamplesDataType;
   isLastMessage: boolean;
   status?: MessageActionStatusType;
   minHeight?: string;
}

const MessageBubble = React.forwardRef<HTMLDivElement, IMessageBubbleProps>(
   ({ role, text, examplesType, examples, isLastMessage, status, minHeight }, ref) => {
      const dispatch = useAppDispatch();
      const { selectedCharities } = useAppSelector(bonoBotState);
      const hydrateTextWithStrongTags = (t: string) => {
         if (!t.includes('**')) {
            return t;
         }

         const parts = t.split(/(\*\*.*?\*\*)/g);

         return parts.map((part) => {
            if (part.startsWith('**') && part.endsWith('**')) {
               const boldText = part.slice(2, -2);
               return (
                  <Typography key={`bold-${boldText}-${Math.random()}`} component='span' sx={{ fontWeight: 700 }}>
                     {boldText}
                  </Typography>
               );
            }
            return part || null;
         });
      };

      useEffect(() => {
         if (status === 'amount_needed') {
            console.log('sending amount message');

            dispatch(
               addMessage({
                  role: 'bot',
                  text: '',
                  examplesType: 'amount',
                  examples: { amount: 20 },
               })
            );
         }
      }, [examplesType, status]);

      // eslint-disable-next-line no-nested-ternary
      return role === 'bot' ? (
         examplesType !== 'amount' || (examplesType === 'amount' && selectedCharities.length > 0) ? (
            <Box
               ref={ref}
               sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '10px',
                  pb: '10px',
                  minHeight: isLastMessage ? minHeight : undefined,
               }}
            >
               {/* message bubble */}
               {text && (
                  <Box
                     sx={{
                        maxWidth: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'start',
                        columnGap: '10px',
                     }}
                  >
                     <BotChatIcon />
                     <Box>
                        <Box>
                           <Typography
                              sx={{
                                 whiteSpace: 'pre-line',
                                 color: '#666666',
                                 fontSize: '16px',
                                 leading: '19px',
                                 fontWeight: 600,
                                 maxWidth: '100%',
                                 wordBreak: 'break-word',
                                 overflowWrap: 'break-word',
                              }}
                           >
                              {hydrateTextWithStrongTags(text)}
                           </Typography>
                        </Box>
                     </Box>
                  </Box>
               )}
               {/* examples */}
               {((examplesType !== 'charity' && isLastMessage) || examplesType === 'charity') &&
                  examples &&
                  examplesType && (
                     <Examples isLastMessage={isLastMessage} status={status} examples={examples} type={examplesType} />
                  )}
            </Box>
         ) : null
      ) : (
         <Box
            ref={ref}
            sx={{
               maxWidth: '100%',
               width: '100%',
               display: 'flex',
               flexDirection: 'row',
               justifyContent: 'end',
               pb: '10px',
            }}
         >
            <Box
               sx={{
                  backgroundColor: '#EFCAC9',
                  px: 2,
                  py: 1.5,
                  border: '1px solid #E5E5E5',
                  borderRadius: '10px',
                  borderTopRightRadius: 0,
               }}
            >
               <Box>
                  <Typography
                     sx={{
                        maxWidth: '100%',
                        whiteSpace: 'pre-line',
                        color: '#666666',
                        fontSize: '16px',
                        leading: '19px',
                        fontWeight: 600,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                     }}
                  >
                     {hydrateTextWithStrongTags(text)}
                  </Typography>
               </Box>
            </Box>
         </Box>
      );
   }
);

MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;
